import * as React from "react";
import { Button } from "../../../ui/components/Button";
import {
  buildDisabledStatusDescription,
  formatDisabledByLabel,
  formatDisabledTimestamp,
  getDisabledStatusTitle,
  type DisabledBySnapshot,
  type DisabledEntityKind,
} from "../../lib/disabled-status";
import * as styles from "./DisabledAccountStatusPanel.css";
import { useI18n } from "../../lib/i18n";

export interface DisabledAccountStatusPanelProps {
  kind: DisabledEntityKind;
  snapshot: DisabledBySnapshot;
  canRestore?: boolean;
  restoreLabel?: string;
  restoreIcon?: React.ReactNode;
  onRestore?: () => void;
}

export function DisabledAccountStatusPanel({
  kind,
  snapshot,
  canRestore = false,
  restoreLabel,
  restoreIcon,
  onRestore,
}: DisabledAccountStatusPanelProps) {
  const { t, tx } = useI18n();
  const title = getDisabledStatusTitle(kind);
  const description = buildDisabledStatusDescription(kind, { canRestore });
  const disabledAt = formatDisabledTimestamp(snapshot.deletedAt);
  const disabledBy = formatDisabledByLabel(snapshot);
  const defaultRestoreLabel =
    kind === "user" ? t("account.disabledPanel.restoreUser") : t("account.disabledPanel.restoreOrg");

  return (
    <div className={styles.panel}>
      <div className={styles.headerRow}>
        <h4 className={styles.title}>{title}</h4>
      </div>
      <p className={styles.description}>{description}</p>
      {(disabledAt || disabledBy) && (
        <dl className={styles.metaList}>
          {disabledAt && (
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>{tx("account.disabledPanel.disabledLabel")}</dt>
              <dd className={styles.metaValue}>{disabledAt}</dd>
            </div>
          )}
          {disabledBy && (
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>{tx("account.disabledPanel.disabledByLabel")}</dt>
              <dd className={styles.metaValue}>{disabledBy}</dd>
            </div>
          )}
        </dl>
      )}
      {canRestore && onRestore && (
        <div className={styles.actions}>
          <Button
            type="button"
            color="primary"
            variant="outline"
            iconLeft={restoreIcon}
            onClick={onRestore}
          >
            {restoreLabel ?? defaultRestoreLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
